import type {
  CanvasEdge,
  CanvasGeometry,
  CanvasMode,
  CanvasNode,
  CanvasNodeType,
  DomainCanvas,
  EntityId,
  JsonValue,
  ReferenceTarget,
} from "./types";
import { CURRENT_CONTENT_SCHEMA_VERSION } from "@note-app/contracts";

/** @deprecated Prefer CURRENT_CONTENT_SCHEMA_VERSION from @note-app/contracts. */
export const CURRENT_CANVAS_SCHEMA_VERSION = CURRENT_CONTENT_SCHEMA_VERSION;

let fallbackIdCounter = 0;

export function createCanvasId(prefix = "canvas"): EntityId {
  if (
    typeof globalThis.crypto === "object" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return globalThis.crypto.randomUUID();
  }
  fallbackIdCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${fallbackIdCounter.toString(36)}`;
}

export function createEmptyCanvas(mode: CanvasMode = "freeform"): DomainCanvas {
  return {
    schemaVersion: CURRENT_CANVAS_SCHEMA_VERSION,
    mode,
    nodes: [],
    edges: [],
    settings: {},
  };
}

export function createGeometry(
  geometry: Partial<CanvasGeometry> = {},
): CanvasGeometry {
  return {
    x: geometry.x ?? 0,
    y: geometry.y ?? 0,
    width: geometry.width ?? 220,
    height: geometry.height ?? 140,
    rotation: geometry.rotation ?? 0,
    zIndex: geometry.zIndex ?? "a0",
  };
}

export interface CreateCanvasNodeInput {
  id?: EntityId;
  type: CanvasNodeType | (string & {});
  geometry?: Partial<CanvasGeometry>;
  props?: Record<string, JsonValue>;
  reference?: ReferenceTarget;
  parentFrameId?: EntityId;
  readingOrder?: number;
}

export function createCanvasNode(input: CreateCanvasNodeInput): CanvasNode {
  const node: CanvasNode = {
    id: input.id ?? createCanvasId("node"),
    type: input.type,
    geometry: createGeometry(input.geometry),
    props: cloneJsonRecord(input.props ?? {}),
  };
  if (input.reference !== undefined) node.reference = { ...input.reference };
  if (input.parentFrameId !== undefined) {
    node.parentFrameId = input.parentFrameId;
  }
  if (input.readingOrder !== undefined) {
    node.readingOrder = input.readingOrder;
  }
  return node;
}

export interface CreateCanvasEdgeInput {
  id?: EntityId;
  sourceNodeId: EntityId;
  targetNodeId: EntityId;
  relationType?: string;
  label?: string;
  props?: Record<string, JsonValue>;
}

export function createCanvasEdge(input: CreateCanvasEdgeInput): CanvasEdge {
  const edge: CanvasEdge = {
    id: input.id ?? createCanvasId("edge"),
    sourceNodeId: input.sourceNodeId,
    targetNodeId: input.targetNodeId,
    props: cloneJsonRecord(input.props ?? {}),
  };
  if (input.relationType !== undefined) {
    edge.relationType = input.relationType;
  }
  if (input.label !== undefined) edge.label = input.label;
  return edge;
}

export function cloneCanvas(canvas: DomainCanvas): DomainCanvas {
  return {
    ...canvas,
    nodes: canvas.nodes.map((node) => ({
      ...node,
      geometry: { ...node.geometry },
      props: cloneJsonRecord(node.props),
      ...(node.reference === undefined
        ? {}
        : { reference: { ...node.reference } }),
    })),
    edges: canvas.edges.map((edge) => ({
      ...edge,
      props: cloneJsonRecord(edge.props),
    })),
    settings: cloneJsonRecord(canvas.settings),
  };
}

export function cloneJsonRecord(
  value: Record<string, JsonValue>,
): Record<string, JsonValue> {
  return JSON.parse(JSON.stringify(value)) as Record<string, JsonValue>;
}

export function referenceTargetKey(target: ReferenceTarget): string {
  return `${target.kind}:${target.id}`;
}

export function nodeBounds(node: CanvasNode): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  const { x, y, width, height } = node.geometry;
  return {
    minX: Math.min(x, x + width),
    minY: Math.min(y, y + height),
    maxX: Math.max(x, x + width),
    maxY: Math.max(y, y + height),
  };
}

/** Axis-aligned bounds of the displayed, rotated object. */
export function rotatedNodeBounds(node: CanvasNode) {
  const { x, y, width, height, rotation } = node.geometry;
  const angle = rotation * Math.PI / 180;
  const halfWidth = (Math.abs(Math.cos(angle)) * width + Math.abs(Math.sin(angle)) * height) / 2;
  const halfHeight = (Math.abs(Math.sin(angle)) * width + Math.abs(Math.cos(angle)) * height) / 2;
  return { minX: x + width / 2 - halfWidth, minY: y + height / 2 - halfHeight, maxX: x + width / 2 + halfWidth, maxY: y + height / 2 + halfHeight };
}

export function isKnownCanvasNodeType(type: string): type is CanvasNodeType {
  return [
    "sticky",
    "text",
    "shape",
    "drawing",
    "diagram",
    "image",
    "frame",
    "reference",
  ].includes(type);
}
